import { useTranslation, Trans } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { pipe, map, values, filter } from 'ramda'
import styled from 'styled-components'
import { select } from '@/store'
import { Button } from '@/components/button'
import { DefaultButtonBar } from '../custom-contract-button-bar'
import ModalContent from '../modal-content'
import { showDocumentModal } from '../modal'

const Paragraph = styled.p`
  text-align: left;
`

const CreateContractButton = ({
  isLastPage,
  isEdit,
  fileMetadata,
  setFileMetadata,
  formattedFiles,
  contractId,
  pages,
  currentDocument,
  currentIndex,
  setSelectedPage,
  handleBack
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const theme = isLastPage || isEdit ? 'primary' : 'secondary'
  const createContractLabel =
    isLastPage || isEdit
      ? t('l.contract_edit.application_contract_edit.create_contract.button', 'Create Contract')
      : t('l.contract_edit.application_contract_edit.next_document.button', 'Next Document')

  const { packageFiles = [] } = useSelector(state =>
    select.contract.contractPackageInfo(state, { contractId })
  )

  const putMetadata = async (fileId, pages) => {
    const { response } = await dispatch.contract.putFieldMetadata({
      packageFileId: fileId,
      contractId: contractId,
      metadata: { fields_metadata: { pages: pages } }
    })
    return response?.ok
  }

  const handleSaveStatementOfResponsibility = async fileId => {
    const response = await dispatch.contract.saveStatementOfResponsibility({
      contractId: contractId,
      packageFileId: fileId
    })
    return response
  }

  const handleConfirmStatement = async () => {
    const documentRequests = pipe(
      values,
      map(async file => {
        await handleSaveStatementOfResponsibility(file?.id)
      })
    )(packageFiles)

    const results = await Promise.all(documentRequests)
    if (results) {
      handleConfirm()
    }
  }

  const handleConfirm = async () => {
    if (!isEdit) {
      const pageRequests = pipe(
        Object.entries,
        map(async ([key, value]) => {
          await putMetadata(key, value)
        })
      )(pages)

      const results = await Promise.all(pageRequests)

      if (filter(Boolean, results)) {
        showDocumentModal({
          title: t('l.contract_edit.document_created.title', 'Document Created!'),
          content: (
            <>
              <Trans t={t} i18nKey="l.contract_manager.documents.document_created_modal.content">
                <Paragraph>
                  Congratulations! You have successfully created a new document. It's ready to be
                  shared with the recipients as soon as you've completed a final review.
                </Paragraph>
                <Paragraph>
                  If you're including additional documents to be signed, you can attach those now.
                </Paragraph>
              </Trans>
            </>
          ),
          confirmCta: t('l.contract_edit.manage_documents.text', 'Manage Documents'),
          backgroundColor: '#EEFCF9',
          confirmFn: () =>
            history.push({
              pathname: `/contract/manager/${fileMetadata['contract_id']}`
            })
        })
      }
    } else {
      const result = await putMetadata(currentDocument?.id, pages?.[currentDocument?.id])
      if (result) {
        history.push({
          pathname: `/contract/manager/${fileMetadata['contract_id']}`
        })
      }
    }
  }
  const handleNext = async () => {
    if (isLastPage || isEdit) {
      showDocumentModal({
        title: t(
          'l.contract_edit.application_contract_edit.legality.title',
          'The legality of this document is your responsibility.'
        ),
        content: <ModalContent />,
        confirmCta: t('l.contract_edit.application_contract_edit.confirm.cta', 'I confirm'),
        cancelCta: t('common.cancel', 'Cancel'),
        backgroundColor: '#fcf5d8',
        confirmFn: handleConfirmStatement
      })
    } else {
      const nextDocument = formattedFiles?.[currentIndex + 1]?.id
      setFileMetadata({ ...fileMetadata, current_document: nextDocument })
      setSelectedPage(0)
    }
  }

  return (
    <DefaultButtonBar handleBack={handleBack} isSigning={false} isInitial={false} sticky>
      <Button theme={theme} size="small" type="submit" onClick={handleNext}>
        {createContractLabel}
      </Button>
    </DefaultButtonBar>
  )
}

export default CreateContractButton
